// Individual task creation (RF-TASK-01) — used by "Fix a problem" and the Kanban.
// The original text is kept by the server; Idempotency-Key is fixed per opened form.
// Placement: Backlog (default) / Prioritize — next to start / Start immediately. The server
// decides and returns `placement_result`; a weaker outcome is shown with its reason.
import { useEffect, useId, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { RISK_LEVELS, TASK_PLACEMENTS, TASK_TYPES } from "@maestros/contracts/enums";
import type { ProjectSummary, RiskLevel, TaskPlacement, TaskType } from "@maestros/contracts";
import { useI18n } from "../i18n/index.tsx";
import { createTask, listProjects, type CreatedTask } from "../api/endpoints.ts";
import { isApiError } from "../api/client.ts";
import { newId } from "../lib/storage.ts";
import { statusView } from "../lib/status.ts";
import { Dialog } from "../components/Dialog.tsx";
import { Button } from "../components/Button.tsx";
import { SelectField, TextAreaField, TextField } from "../components/Field.tsx";
import { Banner, localErrorMessage } from "../components/Feedback.tsx";
import { useToast } from "../components/Toast.tsx";

interface Props {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  defaultType?: TaskType;
  onCreated?: () => void;
}

export function CreateTaskDialog({ open, onClose, projectId, defaultType = "feature", onCreated }: Props) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [key, setKey] = useState(newId);
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [projectsError, setProjectsError] = useState<unknown>(null);
  const [target, setTarget] = useState(projectId ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [evidence, setEvidence] = useState("");
  const [type, setType] = useState<TaskType>(defaultType);
  const [risk, setRisk] = useState<RiskLevel>("medium");
  const [placement, setPlacement] = useState<TaskPlacement>("backlog");
  /** Created task whose placement fell short (or carries a caveat): shown before closing. */
  const [result, setResult] = useState<CreatedTask | null>(null);
  const placementName = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKey(newId());
    setType(defaultType);
    setPlacement("backlog");
    setResult(null);
    setErrors({});
    setSubmitError(null);
    if (!projectId) {
      setProjects(null);
      setProjectsError(null);
      listProjects({ limit: 100 })
        .then((p) => {
          setProjects(p.items);
          setTarget((cur) => cur || p.items[0]?.id || "");
        })
        .catch((e: unknown) => setProjectsError(e));
    } else {
      setTarget(projectId);
    }
  }, [open, projectId, defaultType]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!target) next["project"] = t("validation.chooseProject");
    if (!title.trim()) next["title"] = t("validation.required");
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const task = await createTask(
        target,
        { title: title.trim(), description, evidence, type, risk, origin: "manual", ...(placement !== "backlog" ? { placement } : {}) },
        key,
      );
      setTitle("");
      setDescription("");
      setEvidence("");
      onCreated?.();
      const r = task.placement_result;
      if (r && r.requested !== "backlog" && r.applied !== "started" && r.reason_code !== null) {
        // Kept open: the reason must be readable, not a toast that disappears.
        setResult(task);
        return;
      }
      toast(
        r?.applied === "started"
          ? t("createTask.createdStarted", { number: task.number })
          : r?.applied === "prioritized"
            ? t("createTask.createdPrioritized", { number: task.number })
            : t("createTask.created", { number: task.number }),
        "success",
      );
      finish();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  function finish() {
    setResult(null);
    onClose();
    if (!projectId) navigate(`/projects/${target}/kanban`);
  }

  const formId = "create-task-form";
  if (result?.placement_result) {
    const r = result.placement_result;
    return (
      <Dialog
        open={open}
        onClose={finish}
        title={r.requested === "start_now" ? t("createTask.resultTitleNotStarted", { number: result.number }) : t("createTask.resultTitlePrioritized", { number: result.number })}
        footer={
          <Button variant="primary" onClick={finish} autoFocus>
            {t("common.close")}
          </Button>
        }
      >
        <div data-testid="placement-result">
          <Banner tone="warning" role="status" title={r.reason_code ? t(`placementReason.${r.reason_code}`) : null} meta={r.reason ? `${t("createTask.resultDetail")}: ${r.reason}` : null}>
            {t("createTask.resultStatus", { status: t(statusView(result.status).label) })}
          </Banner>
        </div>
      </Dialog>
    );
  }
  const noProjects = !projectId && projects !== null && projects.length === 0;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      title={defaultType === "bugfix" ? t("createTask.fixTitle") : t("createTask.title")}
      description={t("createTask.description")}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={busy} disabled={noProjects}>
            {submitError && isApiError(submitError) && submitError.retryable ? t("common.retry") : t("createTask.submit")}
          </Button>
        </>
      }
    >
      <form id={formId} className="stack" onSubmit={onSubmit} noValidate>
        {submitError ? (
          <Banner tone="error" role="alert" title={t("createTask.failed")}>
            {localErrorMessage(submitError, t)}
          </Banner>
        ) : null}
        {!projectId ? (
          projectsError ? (
            <Banner tone="error" role="alert">
              {t("createTask.projectsFailed")}
            </Banner>
          ) : noProjects ? (
            <Banner tone="warning">{t("createTask.noProjects")}</Banner>
          ) : (
            <SelectField label={t("createTask.project")} value={target} onChange={(e) => setTarget(e.target.value)} error={errors["project"] ?? null} disabled={projects === null}>
              {projects === null ? <option value="">{t("common.loading")}</option> : null}
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
          )
        ) : null}
        <TextField label={t("task.title")} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} error={errors["title"] ?? null} required autoFocus />
        <TextAreaField label={t("task.descriptionField")} optionalLabel={t("common.optional")} value={description} onChange={(e) => setDescription(e.target.value)} rows={4} hint={t("createTask.originalKept")} />
        <TextAreaField label={t("task.evidence")} optionalLabel={t("common.optional")} value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={2} hint={t("createTask.evidenceHint")} />
        <div className="form-grid">
          <SelectField label={t("task.type")} value={type} onChange={(e) => setType(e.target.value as TaskType)}>
            {TASK_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`taskType.${ty}`)}
              </option>
            ))}
          </SelectField>
          <SelectField label={t("task.risk")} value={risk} onChange={(e) => setRisk(e.target.value as RiskLevel)}>
            {RISK_LEVELS.map((r) => (
              <option key={r} value={r}>
                {t(`risk.${r}`)}
              </option>
            ))}
          </SelectField>
        </div>
        <fieldset className="field" data-testid="placement" aria-describedby={`${placementName}-role`}>
          <legend className="field-label">{t("createTask.placement")}</legend>
          {TASK_PLACEMENTS.map((pl) => (
            <label key={pl} className="choice">
              <input type="radio" name={placementName} value={pl} checked={placement === pl} onChange={() => setPlacement(pl)} aria-describedby={`${placementName}-${pl}`} />
              <span>
                {t(`createTask.placement.${pl}`)}
                <br />
                <span id={`${placementName}-${pl}`} className="field-hint">
                  {t(`createTask.placement.${pl}Hint`)}
                </span>
              </span>
            </label>
          ))}
          <p id={`${placementName}-role`} className="field-hint">
            {t("createTask.placement.roleHint")}
          </p>
        </fieldset>
      </form>
    </Dialog>
  );
}
